import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  DatePicker,
  Select,
  Checkbox,
  Button,
  Space,
  Row,
  Col,
  Divider,
  Typography,
  Alert,
  message,
  Card,
  Spin,
  Tag,
  List
} from 'antd';
import {
  FilePdfOutlined,
  DownloadOutlined,
  LoadingOutlined,
  FileTextOutlined,
  UserOutlined,
  BarChartOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import mapeamentosService from '../../../services/dashboard/mapeamentos.service';
import mapeamentosPdfGenerator from '../../../services/mapeamentosPdfGenerator';
import {
  Questionario,
  TipoRelatorio
} from '../../../types/dashboard/mapeamentos.types';

type EventValue<DateType> = DateType | null;
type RangeValue<DateType> = [EventValue<DateType>, EventValue<DateType>] | null;

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface MapeamentosRelatorioModalProps {
  visible: boolean;
  onCancel: () => void;
  clienteId?: string;
  clienteNome?: string;
  questionarios?: Questionario[];
  loading?: boolean;
}

export interface RelatorioOptions {
  periodo: {
    inicio: string;
    fim: string;
  };
  tipos: TipoRelatorio[];
  questionariosIncluidos: string[];
  incluirDetalhes: boolean;
  incluirObservacoes: boolean;
  incluirEstatisticas: boolean;
  incluirDadosSensiveis: boolean;
  formato: 'pdf';
}

const MapeamentosRelatorioModal: React.FC<MapeamentosRelatorioModalProps> = ({
  visible,
  onCancel,
  clienteId,
  clienteNome,
  questionarios: questionariosProp = [],
  loading: loadingProp = false
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [questionarios, setQuestionarios] = useState<Questionario[]>([]);
  const [loadingQuestionarios, setLoadingQuestionarios] = useState(false);
  const [estatisticasPreview, setEstatisticasPreview] = useState<any>(null);

  const [options, setOptions] = useState<RelatorioOptions>({
    periodo: {
      inicio: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
      fim: dayjs().format('YYYY-MM-DD')
    },
    tipos: [TipoRelatorio.GERAL],
    questionariosIncluidos: [],
    incluirDetalhes: true,
    incluirObservacoes: true,
    incluirEstatisticas: true,
    incluirDadosSensiveis: true,
    formato: 'pdf'
  });

  const tipoOptions = [
    {
      value: TipoRelatorio.GERAL,
      label: 'Relatório Geral',
      description: 'Visão geral completa com questionários, titulares e estatísticas',
      icon: <BarChartOutlined style={{ color: '#1890ff' }} />,
      recomendado: true
    },
    {
      value: TipoRelatorio.QUESTIONARIO_DETALHADO,
      label: 'Questionários Detalhados',
      description: 'Análise detalhada de todos os questionários do cliente',
      icon: <FileTextOutlined style={{ color: '#52c41a' }} />
    },
    {
      value: TipoRelatorio.TITULARES,
      label: 'Relatório de Titulares',
      description: 'Detalhamento completo dos titulares de dados cadastrados',
      icon: <UserOutlined style={{ color: '#faad14' }} />
    },
    {
      value: TipoRelatorio.ESTATISTICAS,
      label: 'Estatísticas e Conformidade',
      description: 'Análise estatística, conformidade LGPD e recomendações',
      icon: <BarChartOutlined style={{ color: '#722ed1' }} />
    }
  ];

  useEffect(() => {
    if (visible && clienteId) {
      carregarQuestionariosCliente();
      carregarEstatisticasPreview();
    }
  }, [visible, clienteId]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setOptions({
        periodo: {
          inicio: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
          fim: dayjs().format('YYYY-MM-DD')
        },
        tipos: [TipoRelatorio.GERAL],
        questionariosIncluidos: [],
        incluirDetalhes: true,
        incluirObservacoes: true,
        incluirEstatisticas: true,
        incluirDadosSensiveis: true,
        formato: 'pdf'
      });
    }
  }, [visible, form]);

  useEffect(() => {
    if (questionarios.length > 0) {
      setOptions(prev => ({
        ...prev,
        questionariosIncluidos: questionarios.map(q => q._id)
      }));
    }
  }, [questionarios]);

  const carregarQuestionariosCliente = async () => {
    if (!clienteId) return;

    setLoadingQuestionarios(true);
    try {
      const response = await mapeamentosService.listarQuestionarios({ cliente_id: clienteId });
      
      let questionariosArray: Questionario[] = [];
      if (Array.isArray(response)) {
        questionariosArray = response;
      } else if (response && typeof response === 'object' && 'data' in response) {
        questionariosArray = (response as any).data || [];
      }
      
      setQuestionarios(questionariosArray);
      
    } catch (error) {
      message.warning('Erro ao carregar questionários do cliente');
      setQuestionarios([]);
    } finally {
      setLoadingQuestionarios(false);
    }
  };

  const carregarEstatisticasPreview = async () => {
    if (!clienteId) return;

    try {
      const response = await mapeamentosService.obterEstatisticas(clienteId);
      if (response && response.success && response.data) {
        setEstatisticasPreview(response.data);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const abrirPDFEmNovaAba = (blob: Blob, nomeArquivo: string) => {
    const url = URL.createObjectURL(blob);
    
    try {
      const screenWidth = window.screen?.availWidth || 1920;
      const screenHeight = window.screen?.availHeight || 1080;
      
      const windowFeatures = [
        `width=${Math.min(1400, screenWidth * 0.9)}`,
        `height=${Math.min(1000, screenHeight * 0.9)}`,
        `left=${Math.round((screenWidth - 1400) / 2)}`,
        `top=${Math.round((screenHeight - 1000) / 2)}`,
        'toolbar=yes',
        'location=yes',
        'status=yes',
        'menubar=yes',
        'scrollbars=yes',
        'resizable=yes'
      ].join(',');

      let newWindow: Window | null = null;
      
      try {
        newWindow = window.open('about:blank', `relatorio_${Date.now()}`, windowFeatures);
      } catch (popupError) {
        // Handle popup blocked silently
      }
      
      if (newWindow && !newWindow.closed) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${nomeArquivo}</title>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              body { 
                margin: 0; 
                padding: 0; 
                background-color: #f5f5f5; 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
              }
              .container { 
                display: flex; 
                flex-direction: column; 
                height: 100vh; 
              }
              .header { 
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                padding: 12px 20px; 
                box-shadow: 0 2px 10px rgba(0,0,0,0.1); 
                display: flex; 
                justify-content: space-between; 
                align-items: center; 
              }
              .header h1 { 
                margin: 0; 
                font-size: 16px; 
                font-weight: 600;
              }
              .header button { 
                padding: 8px 16px; 
                border: 1px solid rgba(255,255,255,0.3); 
                background: rgba(255,255,255,0.1); 
                color: white;
                border-radius: 6px; 
                cursor: pointer; 
                font-size: 12px; 
                margin-left: 8px;
                transition: background 0.2s;
              }
              .header button:hover { 
                background: rgba(255,255,255,0.2); 
              }
              .header button.primary { 
                background: rgba(255,255,255,0.9); 
                color: #333;
              }
              .client-info {
                background: #e6f7ff;
                padding: 8px 20px;
                border-bottom: 1px solid #91d5ff;
                font-size: 12px;
                color: #1890ff;
                text-align: center;
              }
              iframe { 
                flex: 1; 
                width: 100%; 
                border: none; 
                background: white;
              }
              .loading {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 200px;
                color: #666;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h1>📊 ${nomeArquivo}</h1>
                <div>
                  <button onclick="window.print()" title="Imprimir relatório">🖨️ Imprimir</button>
                  <button onclick="downloadPDF()" class="primary" title="Baixar arquivo PDF">💾 Baixar</button>
                  <button onclick="window.close()" title="Fechar janela">✖️ Fechar</button>
                </div>
              </div>
              <div class="client-info">
                📋 Relatório de Mapeamento LGPD - Cliente: ${clienteNome || 'Cliente selecionado'}
              </div>
              <div class="loading" id="loading">Carregando relatório...</div>
              <iframe src="${url}" style="display: none;" onload="showPDF()" onerror="showError()"></iframe>
            </div>
            
            <script>
              function downloadPDF() {
                const link = document.createElement('a');
                link.href = '${url}';
                link.download = '${nomeArquivo}';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
              
              function showPDF() {
                document.getElementById('loading').style.display = 'none';
                document.querySelector('iframe').style.display = 'block';
              }
              
              function showError() {
                document.getElementById('loading').innerHTML = 'Erro ao carregar o relatório';
              }
              
              window.addEventListener('beforeunload', function() {
                URL.revokeObjectURL('${url}');
              });
              
              setTimeout(() => {
                URL.revokeObjectURL('${url}');
              }, 120000);
            </script>
          </body>
          </html>
        `);
        
        newWindow.document.close();
        newWindow.focus();
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } else {
        const tabWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (tabWindow) {
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else {
          const link = document.createElement('a');
          link.href = url;
          link.download = nomeArquivo;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          message.info('O relatório foi baixado automaticamente');
        }
      }
    } catch (error) {
      const link = document.createElement('a');
      link.href = url;
      link.download = nomeArquivo;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.warning('Relatório baixado automaticamente devido a restrições do navegador');
    }
  };

  const handleGerarRelatorio = async () => {
    try {
      const values = await form.validateFields();
      
      if (options.tipos.length === 0) {
        message.warning('Selecione pelo menos um tipo de relatório');
        return;
      }

      if (!clienteId) {
        message.error('ID do cliente não informado');
        return;
      }

      setLoading(true);
      const messageKey = 'generating-report';
      message.loading({ content: 'Preparando relatório...', key: messageKey, duration: 0 });

      let tipoRelatorio = 'geral';
      if (options.tipos.length === 1) {
        tipoRelatorio = options.tipos[0];
      } else if (options.tipos.includes(TipoRelatorio.GERAL)) {
        tipoRelatorio = 'geral';
      }

      const filtros = {
        tipo: tipoRelatorio,
        nomeCliente: clienteNome,
        incluir_dados_sensiveis: options.incluirDadosSensiveis,
        incluir_observacoes: options.incluirObservacoes,
        incluir_estatisticas: options.incluirEstatisticas,
        incluir_detalhes: options.incluirDetalhes,
        questionarios_incluidos: options.questionariosIncluidos,
        data_inicio: options.periodo.inicio,
        data_fim: options.periodo.fim,
        total_tipos_selecionados: options.tipos.length
      };

      message.loading({ content: 'Coletando dados do cliente...', key: messageKey, duration: 0 });

      let pdfBlob: Blob;

      if (options.tipos.length > 1) {
        message.loading({ content: 'Gerando relatório múltiplo...', key: messageKey, duration: 0 });
        
        if (typeof mapeamentosPdfGenerator.gerarRelatorioMultiplo === 'function') {
          pdfBlob = await mapeamentosPdfGenerator.gerarRelatorioMultiplo(
            clienteId,
            options.tipos,
            filtros
          );
        } else {
          pdfBlob = await mapeamentosPdfGenerator.gerarRelatorioDashboard(
            clienteId,
            filtros
          );
        }
      } else {
        message.loading({ content: 'Gerando relatório PDF...', key: messageKey, duration: 0 });
        
        pdfBlob = await mapeamentosPdfGenerator.gerarRelatorioDashboard(
          clienteId,
          filtros
        );
      }

      const timestamp = dayjs().format('YYYY-MM-DD-HH-mm');
      const clienteParaArquivo = (clienteNome || 'cliente').replace(/[^a-zA-Z0-9]/g, '-');
      const tipoParaArquivo = options.tipos.length > 1 ? 'completo' : tipoRelatorio;
      const nomeArquivo = `relatorio-mapeamento-${tipoParaArquivo}-${clienteParaArquivo}-${timestamp}.pdf`;

      abrirPDFEmNovaAba(pdfBlob, nomeArquivo);

      message.destroy(messageKey);
      message.success('Relatório gerado com sucesso!');
      onCancel();

    } catch (error) {
      message.destroy();
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      message.error(`Erro ao gerar relatório: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const questionariosIncluidos = questionarios.filter(q => 
    options.questionariosIncluidos.includes(q._id)
  );

  return (
    <Modal
      title={
        <Space>
          <FilePdfOutlined />
          Gerar Relatório de Mapeamento
          {clienteNome && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {clienteNome}
            </Tag>
          )}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>,
        <Button
          key="generate"
          type="primary"
          icon={loading ? <LoadingOutlined /> : <DownloadOutlined />}
          onClick={handleGerarRelatorio}
          loading={loading}
          disabled={options.tipos.length === 0 || loading}
        >
          Gerar Relatório
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message={`Relatório será gerado para: ${clienteNome || 'Cliente selecionado'}`}
          description={
            questionarios.length > 0 
              ? `${questionarios.length} questionário(s) disponível(eis) para este cliente`
              : 'Nenhum questionário encontrado para este cliente'
          }
          type={questionarios.length > 0 ? "info" : "warning"}
          showIcon
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          periodo: [dayjs().subtract(30, 'days'), dayjs()],
          tipos: [TipoRelatorio.GERAL],
          incluirDetalhes: true,
          incluirObservacoes: true,
          incluirEstatisticas: true,
          incluirDadosSensiveis: true
        }}
      >
        <Form.Item
          name="periodo"
          label="Período de Análise"
          rules={[{ required: true, message: 'Selecione o período' }]}
        >
          <RangePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder={['Data inicial', 'Data final']}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                const startDate = dates[0];
                const endDate = dates[1];
                
                if (startDate && endDate) {
                  setOptions(prev => ({
                    ...prev,
                    periodo: {
                      inicio: startDate.format('YYYY-MM-DD'),
                      fim: endDate.format('YYYY-MM-DD')
                    }
                  }));
                }
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="tipos"
          label="Tipos de Relatório"
          rules={[{ required: true, message: 'Selecione pelo menos um tipo' }]}
        >
          <Checkbox.Group
            style={{ width: '100%' }}
            onChange={(values) => setOptions(prev => ({ ...prev, tipos: values as TipoRelatorio[] }))}
          >
            <Row gutter={[16, 16]}>
              {tipoOptions.map(option => (
                <Col xs={24} sm={12} key={option.value}>
                  <Card 
                    size="small" 
                    hoverable
                    style={{ 
                      cursor: 'pointer',
                      border: options.tipos.includes(option.value) ? '2px solid #1890ff' : '1px solid #d9d9d9'
                    }}
                    onClick={() => {
                      const currentTypes = options.tipos;
                      const newTypes = currentTypes.includes(option.value)
                        ? currentTypes.filter(t => t !== option.value)
                        : [...currentTypes, option.value];
                      setOptions(prev => ({ ...prev, tipos: newTypes }));
                    }}
                  >
                    <Space align="start">
                      <Checkbox 
                        value={option.value}
                        checked={options.tipos.includes(option.value)}
                        onChange={(e) => e.stopPropagation()}
                      >
                        <div style={{ marginLeft: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {option.icon}
                            <span style={{ fontWeight: 'bold' }}>{option.label}</span>
                            {(option as any).recomendado && (
                              <Tag color="green" style={{ fontSize: '10px' }}>Recomendado</Tag>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                            {option.description}
                          </div>
                        </div>
                      </Checkbox>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>

        {questionarios.length > 0 && (
          <Form.Item label="Questionários do Cliente">
            <Card size="small" style={{ maxHeight: 200, overflowY: 'auto' }}>
              <Spin spinning={loadingQuestionarios}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong>
                      {questionariosIncluidos.length} de {questionarios.length} questionários selecionados
                    </Text>
                    <Space>
                      <Button 
                        size="small" 
                        onClick={() => setOptions(prev => ({ 
                          ...prev, 
                          questionariosIncluidos: questionarios.map(q => q._id) 
                        }))}
                      >
                        Selecionar Todos
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => setOptions(prev => ({ 
                          ...prev, 
                          questionariosIncluidos: [] 
                        }))}
                      >
                        Limpar
                      </Button>
                    </Space>
                  </div>
                  
                  <Checkbox.Group
                    style={{ width: '100%' }}
                    value={options.questionariosIncluidos}
                    onChange={(values) => setOptions(prev => ({ 
                      ...prev, 
                      questionariosIncluidos: values as string[] 
                    }))}
                  >
                    <List
                      size="small"
                      dataSource={questionarios}
                      renderItem={(questionario) => (
                        <List.Item style={{ padding: '8px 0', borderBottom: 'none' }}>
                          <Checkbox value={questionario._id} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                              <div>
                                <Text strong>{questionario.nome}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {questionario.abordagem} • {dayjs(questionario.created_at).format('DD/MM/YYYY')}
                                </Text>
                              </div>
                              <Tag 
                                color={questionario.inativo ? 'red' : 'green'} 
                                style={{ fontSize: '10px' }}
                              >
                                {questionario.inativo ? 'Inativo' : 'Ativo'}
                              </Tag>
                            </div>
                          </Checkbox>
                        </List.Item>
                      )}
                    />
                  </Checkbox.Group>
                </Space>
              </Spin>
            </Card>
          </Form.Item>
        )}

        <Divider />

        <Title level={5}>
          <InfoCircleOutlined style={{ marginRight: 8 }} />
          Configurações do Relatório
        </Title>
        
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Form.Item name="incluirDetalhes" valuePropName="checked">
              <Checkbox
                checked={options.incluirDetalhes}
                onChange={(e) => setOptions(prev => ({ ...prev, incluirDetalhes: e.target.checked }))}
              >
                <Space direction="vertical" size={0}>
                  <span>Incluir Detalhes</span>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Informações detalhadas de cada item
                  </Text>
                </Space>
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="incluirObservacoes" valuePropName="checked">
              <Checkbox
                checked={options.incluirObservacoes}
                onChange={(e) => setOptions(prev => ({ ...prev, incluirObservacoes: e.target.checked }))}
              >
                <Space direction="vertical" size={0}>
                  <span>Incluir Observações</span>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Comentários e observações cadastradas
                  </Text>
                </Space>
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="incluirEstatisticas" valuePropName="checked">
              <Checkbox
                checked={options.incluirEstatisticas}
                onChange={(e) => setOptions(prev => ({ ...prev, incluirEstatisticas: e.target.checked }))}
              >
                <Space direction="vertical" size={0}>
                  <span>Incluir Estatísticas</span>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Gráficos e indicadores de conformidade
                  </Text>
                </Space>
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="incluirDadosSensiveis" valuePropName="checked">
              <Checkbox
                checked={options.incluirDadosSensiveis}
                onChange={(e) => setOptions(prev => ({ ...prev, incluirDadosSensiveis: e.target.checked }))}
              >
                <Space direction="vertical" size={0}>
                  <span>Incluir Dados Sensíveis</span>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Dados pessoais sensíveis identificados
                  </Text>
                </Space>
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {estatisticasPreview && (
          <Card 
            size="small" 
            title="Preview dos Dados" 
            style={{ marginTop: 16, backgroundColor: '#fafafa' }}
          >
            <Row gutter={[16, 8]}>
              <Col span={8}>
                <Text type="secondary">Questionários:</Text>
                <br />
                <Text strong>{estatisticasPreview.total_questionarios || 0}</Text>
              </Col>
              <Col span={8}>
                <Text type="secondary">Titulares:</Text>
                <br />
                <Text strong>{estatisticasPreview.total_titulares || 0}</Text>
              </Col>
              <Col span={8}>
                <Text type="secondary">Taxa Conclusão:</Text>
                <br />
                <Text strong>{estatisticasPreview.taxa_conclusao || 0}%</Text>
              </Col>
            </Row>
          </Card>
        )}

        {loading && (
          <Alert
            type="info"
            message="Gerando relatório..."
            description="Por favor, aguarde. O relatório será aberto em uma nova aba assim que estiver pronto."
            showIcon
            style={{ marginTop: 16 }}
         />
       )}

       {options.tipos.length > 1 && (
         <Alert
           type="info"
           message="Relatório Múltiplo"
           description={`Será gerado um relatório completo contendo ${options.tipos.length} seções diferentes.`}
           showIcon
           style={{ marginTop: 16 }}
         />
       )}

       {questionarios.length === 0 && !loadingQuestionarios && (
         <Alert
           type="warning"
           message="Nenhum questionário encontrado"
           description="Este cliente não possui questionários cadastrados. O relatório será gerado apenas com dados básicos disponíveis."
           showIcon
           style={{ marginTop: 16 }}
         />
       )}
     </Form>
   </Modal>
 );
};

export default MapeamentosRelatorioModal;